import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Plus, Search } from 'lucide-react';
import { Heading } from '../layout';
import { Dialog } from '../reusable';
import { useUIColors } from '../../hooks/useTheme';
import { FormDrawer } from '../forms/FormDrawer';

const formatMoney = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-CL');
};

const MOCK_QUOTATIONS = [
  {
    id: 1,
    quote_code: 'COT-001',
    supplier_name: 'BRENNTAG',
    supplier_code: 'PRV-007',
    quote_date: '2026-07-10',
    valid_until: '2026-08-10',
    currency: 'CLP',
    status: 'Aceptada',
    buyer_name: 'V. COLL',
    items_count: 3,
    total_amount: 1400522,
    notes: 'Cotización de solventes y aditivos.',
    items: [
      { id: 1, item_code: 'SOL-100', description: 'Solvente aromático', quantity: 20, unit: 'KG', unit_price: 18500, line_total: 370000 },
      { id: 2, item_code: 'AD-22', description: 'Aditivo dispersante', quantity: 8, unit: 'KG', unit_price: 42000, line_total: 336000 },
      { id: 3, item_code: 'RES-01', description: 'Resina acrílica', quantity: 12, unit: 'KG', unit_price: 57877, line_total: 694522 },
    ],
  },
  {
    id: 2,
    quote_code: 'COT-002',
    supplier_name: 'EX CCI',
    supplier_code: 'PRV-002',
    quote_date: '2026-07-18',
    valid_until: '2026-08-18',
    currency: 'USD',
    status: 'Enviada',
    buyer_name: 'E. TOLEDO',
    items_count: 2,
    total_amount: 3120700,
    notes: 'Precios referenciales en USD; total mostrado en CLP estimado.',
    items: [
      { id: 1, item_code: 'IMP-09', description: 'Pigmento importado', quantity: 50, unit: 'KG', unit_price: 2873, line_total: 143650 },
      { id: 2, item_code: 'IMP-10', description: 'Carga especial', quantity: 30, unit: 'KG', unit_price: 1900, line_total: 57000 },
    ],
  },
  {
    id: 3,
    quote_code: 'COT-003',
    supplier_name: 'DEXIMA',
    supplier_code: 'PRV-009',
    quote_date: '2026-07-22',
    valid_until: '2026-08-05',
    currency: 'CLP',
    status: 'Borrador',
    buyer_name: 'V. COLL',
    items_count: 1,
    total_amount: 499800,
    notes: '',
    items: [
      { id: 1, item_code: 'ENV-4', description: 'Envases 20 L', quantity: 100, unit: 'UN', unit_price: 4200, line_total: 420000 },
    ],
  },
  {
    id: 4,
    quote_code: 'COT-004',
    supplier_name: 'QCA. CANDELARIA',
    supplier_code: 'PRV-003',
    quote_date: '2026-06-01',
    valid_until: '2026-06-30',
    currency: 'CLP',
    status: 'Vencida',
    buyer_name: 'E. TOLEDO',
    items_count: 2,
    total_amount: 166314,
    notes: 'No renovada.',
    items: [
      { id: 1, item_code: 'MP-11', description: 'Carbonato de calcio', quantity: 40, unit: 'KG', unit_price: 2200, line_total: 88000 },
      { id: 2, item_code: 'MP-12', description: 'Talco industrial', quantity: 25, unit: 'KG', unit_price: 2070, line_total: 51750 },
    ],
  },
  {
    id: 5,
    quote_code: 'COT-005',
    supplier_name: 'CORVALÁN',
    supplier_code: 'PRV-008',
    quote_date: '2026-08-01',
    valid_until: '2026-08-31',
    currency: 'CLP',
    status: 'Rechazada',
    buyer_name: 'V. COLL',
    items_count: 1,
    total_amount: 15089,
    notes: 'Precio sobre mercado.',
    items: [
      { id: 1, item_code: 'SER-1', description: 'Servicio de muestreo', quantity: 1, unit: 'UN', unit_price: 12680, line_total: 12680 },
    ],
  },
];

const STATUS_STYLE = (status, isDarkMode) => {
  const s = String(status || '').toLowerCase();
  if (s === 'aceptada') {
    return { bg: isDarkMode ? '#14532d' : '#dcfce7', color: isDarkMode ? '#bbf7d0' : '#166534', border: isDarkMode ? '#22c55e' : '#86efac' };
  }
  if (s === 'enviada') {
    return { bg: isDarkMode ? '#1e3a5f' : '#dbeafe', color: isDarkMode ? '#93c5fd' : '#1d4ed8', border: isDarkMode ? '#3b82f6' : '#93c5fd' };
  }
  if (s === 'rechazada' || s === 'vencida') {
    return { bg: isDarkMode ? '#7f1d1d' : '#fee2e2', color: isDarkMode ? '#fca5a5' : '#b91c1c', border: isDarkMode ? '#ef4444' : '#fecaca' };
  }
  return { bg: isDarkMode ? '#27272a' : '#f0ebe3', color: isDarkMode ? '#e4e4e7' : '#44403c', border: isDarkMode ? '#3f3f46' : '#d6d0c4' };
};

const COLUMNS = [
  { key: 'Código', sortKey: 'quote_code', align: 'left' },
  { key: 'Proveedor', sortKey: 'supplier_name', align: 'left' },
  { key: 'Ejecutivo', sortKey: 'buyer_name', align: 'left' },
  { key: 'Fecha', sortKey: 'quote_date', align: 'left' },
  { key: 'Válida hasta', sortKey: 'valid_until', align: 'left' },
  { key: 'Ítems', sortKey: 'items_count', align: 'right' },
  { key: 'Total', sortKey: 'total_amount', align: 'right' },
  { key: 'Estado', sortKey: 'status', align: 'center' },
];

const emptyForm = () => ({
  quote_code: '',
  supplier_name: '',
  buyer_name: '',
  quote_date: new Date().toISOString().slice(0, 10),
  valid_until: '',
  currency: 'CLP',
  status: 'Borrador',
  notes: '',
});

const PaintProcurementQuotationsWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const [rows, setRows] = useState(MOCK_QUOTATIONS);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortColumn, setSortColumn] = useState('quote_date');
  const [sortDir, setSortDir] = useState('desc');
  const [detail, setDetail] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(emptyForm());

  const filtered = useMemo(() => {
    let list = rows;
    const tokens = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length) {
      list = list.filter((row) => {
        const hay = [row.quote_code, row.supplier_name, row.buyer_name, row.status, row.notes]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return tokens.every((t) => hay.includes(t));
      });
    }
    if (filterStatus) list = list.filter((r) => r.status === filterStatus);
    return list;
  }, [rows, searchQuery, filterStatus]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const mult = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      const key = sortColumn;
      let va = a[key];
      let vb = b[key];
      if (key.includes('date') || key === 'valid_until') {
        va = va ? new Date(va).getTime() : 0;
        vb = vb ? new Date(vb).getTime() : 0;
        return (va - vb) * mult;
      }
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mult;
      return String(va || '').localeCompare(String(vb || ''), 'es', { numeric: true }) * mult;
    });
    return arr;
  }, [filtered, sortColumn, sortDir]);

  const statuses = useMemo(
    () => [...new Set(rows.map((r) => r.status).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'es')),
    [rows]
  );

  const handleSort = (key) => {
    if (sortColumn === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortColumn(key);
      setSortDir(key.includes('date') || key === 'total_amount' ? 'desc' : 'asc');
    }
  };

  const SortIcon = ({ columnKey }) => {
    if (sortColumn !== columnKey) return <ArrowUpDown size={12} color={ui.muted} />;
    return sortDir === 'asc' ? <ArrowUp size={12} color={ui.text} /> : <ArrowDown size={12} color={ui.text} />;
  };

  const openCreate = () => {
    setFormData(emptyForm());
    setShowForm(true);
  };

  const handleSave = () => {
    const nextId = Math.max(0, ...rows.map((r) => r.id)) + 1;
    const code = `COT-${String(nextId).padStart(3, '0')}`;
    setRows((prev) => [
      {
        id: nextId,
        quote_code: code,
        supplier_name: formData.supplier_name || 'Proveedor demo',
        supplier_code: '',
        quote_date: formData.quote_date,
        valid_until: formData.valid_until,
        currency: formData.currency || 'CLP',
        status: formData.status || 'Borrador',
        buyer_name: formData.buyer_name || '',
        items_count: 0,
        total_amount: 0,
        notes: formData.notes || '',
        items: [],
      },
      ...prev,
    ]);
    setShowForm(false);
  };

  const selectStyle = {
    height: 36,
    padding: '0 10px',
    borderRadius: 8,
    border: `1px solid ${ui.inputBorder}`,
    background: ui.inputBg,
    color: ui.text,
    fontSize: 13,
    outline: 'none',
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Heading level={2} isDarkMode={isDarkMode} style={{ marginBottom: 6 }}>
          Cotizaciones
        </Heading>
        <p style={{ margin: 0, maxWidth: 680, fontSize: 13, color: ui.muted, lineHeight: 1.45 }}>
          Cotizaciones de proveedores para adquisiciones. Datos de ejemplo (frontend); la API y BD se
          conectarán después.
        </p>
      </div>

      <div style={{ border: `1px solid ${ui.border}`, borderRadius: 12, overflow: 'hidden', background: ui.page }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>Cotizaciones</h3>
            <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>
              {sorted.length} de {rows.length} registros
            </p>
          </div>
          <button
            type="button"
            onClick={openCreate}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 36,
              padding: '0 14px',
              borderRadius: 8,
              border: 'none',
              background: '#16a34a',
              color: '#fff',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            <Plus size={16} /> Nueva cotización
          </button>
        </div>

        <div style={{ padding: '10px 16px', borderBottom: `1px solid ${ui.border}`, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: '1 1 220px' }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: ui.muted }}>Buscar</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Search size={16} color={ui.muted} />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Código, proveedor, ejecutivo…"
                style={{ ...selectStyle, flex: 1, minWidth: 0 }}
              />
            </span>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 140 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: ui.muted }}>Estado</span>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={selectStyle}>
              <option value="">Todos</option>
              {statuses.map((st) => (
                <option key={st} value={st}>
                  {st}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 960 }}>
            <thead>
              <tr style={{ background: ui.header, borderBottom: `1px solid ${ui.border}` }}>
                {COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.sortKey)}
                    style={{
                      padding: '8px 10px',
                      textAlign: col.align,
                      fontSize: 11,
                      fontWeight: 700,
                      color: ui.muted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {col.key}
                      <SortIcon columnKey={col.sortKey} />
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => {
                const st = STATUS_STYLE(row.status, isDarkMode);
                return (
                  <tr key={row.id} style={{ borderBottom: `1px solid ${ui.border}` }}>
                    <td style={{ padding: '9px 10px' }}>
                      <button
                        type="button"
                        onClick={() => setDetail(row)}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          padding: 0,
                          cursor: 'pointer',
                          color: ui.text,
                          fontWeight: 700,
                          fontSize: 12,
                        }}
                      >
                        {row.quote_code}
                      </button>
                    </td>
                    <td style={{ padding: '9px 10px' }}>
                      <div style={{ fontWeight: 700, fontSize: 12, color: ui.text }}>{row.supplier_name}</div>
                      <div style={{ fontSize: 11, color: ui.muted }}>{row.supplier_code}</div>
                    </td>
                    <td style={{ padding: '9px 10px', fontSize: 12, color: ui.text }}>{row.buyer_name || '—'}</td>
                    <td style={{ padding: '9px 10px', fontSize: 12, color: ui.muted }}>{formatDate(row.quote_date)}</td>
                    <td style={{ padding: '9px 10px', fontSize: 12, color: ui.muted }}>{formatDate(row.valid_until)}</td>
                    <td style={{ padding: '9px 10px', fontSize: 12, textAlign: 'right', fontWeight: 600 }}>{row.items_count}</td>
                    <td style={{ padding: '9px 10px', fontSize: 12, textAlign: 'right', fontWeight: 700 }}>
                      {formatMoney(row.total_amount)}
                    </td>
                    <td style={{ padding: '9px 10px', textAlign: 'center' }}>
                      <span
                        style={{
                          display: 'inline-flex',
                          padding: '2px 8px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 600,
                          background: st.bg,
                          color: st.color,
                          border: `1px solid ${st.border}`,
                        }}
                      >
                        {row.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <FormDrawer
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        onSave={handleSave}
        isDarkMode={isDarkMode}
        title="Nueva cotización"
        subtitle="Adquisiciones · demo local"
        formData={formData}
        onChange={(key, value) => setFormData((prev) => ({ ...prev, [key]: value }))}
        width={560}
        sections={[
          {
            key: 'basic',
            title: 'Datos',
            fields: [
              { key: 'supplier_name', label: 'Proveedor' },
              { key: 'buyer_name', label: 'Ejecutivo / Comprador' },
              { key: 'quote_date', label: 'Fecha', type: 'date' },
              { key: 'valid_until', label: 'Válida hasta', type: 'date' },
              {
                key: 'status',
                label: 'Estado',
                type: 'select',
                options: [
                  { value: 'Borrador', label: 'Borrador' },
                  { value: 'Enviada', label: 'Enviada' },
                  { value: 'Aceptada', label: 'Aceptada' },
                  { value: 'Rechazada', label: 'Rechazada' },
                  { value: 'Vencida', label: 'Vencida' },
                ],
              },
              { key: 'currency', label: 'Moneda' },
              { key: 'notes', label: 'Notas', type: 'textarea', fullWidth: true, rows: 3 },
            ],
          },
        ]}
      />

      <Dialog
        isOpen={Boolean(detail)}
        onClose={() => setDetail(null)}
        title="Ficha de cotización"
        subtitle={detail?.quote_code || ''}
        isDarkMode={isDarkMode}
        size="lg"
      >
        {detail ? (
          <div style={{ padding: '0 20px 20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 11, color: ui.muted, textTransform: 'uppercase' }}>Proveedor</div>
                <div style={{ fontWeight: 700, color: ui.text }}>{detail.supplier_name}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: ui.muted, textTransform: 'uppercase' }}>Ejecutivo</div>
                <div style={{ color: ui.text }}>{detail.buyer_name || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: ui.muted, textTransform: 'uppercase' }}>Estado</div>
                <div style={{ color: ui.text }}>{detail.status}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: ui.muted, textTransform: 'uppercase' }}>Total</div>
                <div style={{ fontWeight: 800, color: ui.text }}>{formatMoney(detail.total_amount)}</div>
              </div>
            </div>
            <p style={{ margin: '0 0 12px', fontSize: 13, color: ui.muted }}>{detail.notes || 'Sin notas.'}</p>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: isDarkMode ? '#27272a' : '#f8fafc' }}>
                  {['Código', 'Descripción', 'Cant.', 'Unidad', 'Precio', 'Total'].map((h) => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: ui.muted }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(detail.items || []).map((item) => (
                  <tr key={item.id} style={{ borderTop: `1px solid ${ui.border}` }}>
                    <td style={{ padding: '8px 10px' }}>{item.item_code || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>{item.description}</td>
                    <td style={{ padding: '8px 10px' }}>{item.quantity}</td>
                    <td style={{ padding: '8px 10px' }}>{item.unit}</td>
                    <td style={{ padding: '8px 10px' }}>{formatMoney(item.unit_price)}</td>
                    <td style={{ padding: '8px 10px', fontWeight: 700 }}>{formatMoney(item.line_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
};

export default PaintProcurementQuotationsWorkspace;
